import React, { useState, memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import ReactMarkdown from "react-markdown";
import LoadingText from "../utils/LoadingText";
import Feedback from "../utils/feedback";
import EventError from "../utils/eventError";
import mainLogo from "../../../assets/babylon.png";
import "katex/dist/katex.min.css";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  File,
  RefreshCw,
  X,
} from "lucide-react";
import api, { baseURL } from "../../../utils/api";

const formatLabel = (value = "") =>
  value
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusClass = (status = "") => {
  const normalized = status.toString().toUpperCase();

  if (normalized.includes("COMPLIANT") && !normalized.includes("NON")) {
    return "bg-green-100 text-green-800 ring-green-200 dark:bg-green-900/40 dark:text-green-200 dark:ring-green-700";
  }

  if (normalized.includes("CLARIFICATION") || normalized.includes("MEDIUM")) {
    return "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-700";
  }

  if (normalized.includes("NON_COMPLIANT") || normalized.includes("HIGH")) {
    return "bg-red-100 text-red-800 ring-red-200 dark:bg-red-900/40 dark:text-red-200 dark:ring-red-700";
  }

  return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600";
};

const parseStructuredMessage = (value) => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed?.error) return { type: "error", data: parsed };
    if (Array.isArray(parsed?.documents) || parsed?.summary) {
      return { type: "invoice", data: parsed };
    }
  } catch {
    return null;
  }

  return null;
};

const getFriendlyErrorMessage = (error = "") => {
  const normalized = error.toString().toLowerCase();

  if (normalized.includes("504") || normalized.includes("gateway timeout")) {
    return {
      title: "The invoice analysis took too long",
      description:
        "The analysis service did not respond in time. This can happen when the uploaded file is large, the workflow is busy, or the external automation service is temporarily slow.",
      action:
        "Please try again in a few minutes. If it keeps happening, upload a clearer or smaller file and try once more.",
    };
  }

  if (normalized.includes("expecting value")) {
    return {
      title: "The analysis service returned an empty response",
      description:
        "BETH could not read a valid result from the invoice analysis service.",
      action:
        "Please try again. If the issue continues, the backend workflow may need to be checked.",
    };
  }

  return {
    title: "Something went wrong",
    description:
      "BETH could not complete the request because the analysis service returned an unexpected error.",
    action: "Please try again. If it keeps happening, contact support.",
  };
};

const StatusBadge = ({ children }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap break-normal rounded-full px-2.5 py-1 text-[0.8rem] font-semibold leading-5 ring-1 ${getStatusClass(
      children,
    )}`}>
    {formatLabel(children || "Unknown")}
  </span>
);

const FriendlyErrorResponse = ({ error }) => {
  const friendlyError = getFriendlyErrorMessage(error?.error);

  return (
    <div className="w-full max-w-4xl text-[0.95rem] ">
      <h3 className="text-[1.05rem] font-bold leading-snug">
        {friendlyError.title}
      </h3>
      <p className="mt-2">{friendlyError.description}</p>
      <p className="mt-2 font-semibold">{friendlyError.action}</p>
    </div>
  );
};

const getAttachmentName = (file) => {
  if (typeof file === "string") return file;
  return file?.filename || file?.name || "Attachment";
};

const getFileAccentClass = (filename = "") => {
  const lowerFilename = filename.toLowerCase();

  if (lowerFilename.endsWith(".pdf")) return "bg-red-400";
  if (lowerFilename.endsWith(".docx") || lowerFilename.endsWith(".doc")) {
    return "bg-blue-400";
  }
  if (lowerFilename.match(/\.(png|jpe?g|gif|webp)$/)) return "bg-emerald-400";

  return "bg-orange-400";
};

const getAttachmentType = (file, filename) => {
  const mimeType = typeof file === "string" ? "" : file?.mime_type || "";
  const lowerFilename = filename.toLowerCase();

  if (
    mimeType.startsWith("image/") ||
    lowerFilename.match(/\.(png|jpe?g|gif|webp)$/)
  ) {
    return "Image";
  }

  return "File";
};

const FileAttachmentCard = ({ file }) => {
  const filename = getAttachmentName(file);
  const attachmentType = getAttachmentType(file, filename);

  return (
    <div className="flex justify-end w-full mb-1">
      <div className="flex max-w-sm p-1 text-sm text-gray-800 border border-gray-400 rounded-lg shadow-inner hover:bg-slate-200 w-fit dark:text-gray-100 hover:dark:bg-slate-700">
        <div className="flex min-w-0 items-center gap-2.5 p-1 mr-2">
          <div className={`p-2 rounded-md ${getFileAccentClass(filename)}`}>
            <File
              size={20}
              className="text-gray-200"
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold break-words">{filename}</span>
            <span className="text-xs">{attachmentType}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
const StructuredInvoiceResponse = ({ data }) => (
  <div className="w-full max-w-5xl space-y-4 text-[0.95rem] leading-relaxed text-slate-900 dark:text-slate-100">
    <div className="pb-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-[1.1rem] font-bold leading-snug">
            Invoice Analysis Result
          </h3>
          {data?.summary?.overall_narrative_summary && (
            <p className="max-w-4xl mt-2 leading-relaxed text-slate-700 dark:text-slate-300">
              {data.summary.overall_narrative_summary}
            </p>
          )}
        </div>
        {data?.summary?.escalation_recommended !== undefined && (
          <StatusBadge>
            {data.summary.escalation_recommended
              ? "Escalation Recommended"
              : "No Escalation"}
          </StatusBadge>
        )}
      </div>
    </div>

    {data.documents?.map((document, documentIndex) => (
      <div
        key={document.document_id || documentIndex}
        className="overflow-hidden">
        <div className="py-4 border-y border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-[0.95rem] font-bold leading-snug">
                {document.display_title || document.file_name || "Document"}
              </h4>
              <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-400">
                {[
                  document.file_name,
                  formatLabel(document.document_type || ""),
                  formatLabel(document.route || ""),
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {document.verdict && (
                <StatusBadge>{document.verdict}</StatusBadge>
              )}
              {document.risk_level && (
                <StatusBadge>{document.risk_level}</StatusBadge>
              )}
            </div>
          </div>
        </div>

        {document.table_rows?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed border-collapse text-left text-[0.9rem]">
              <thead className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                <tr>
                  <th className="px-3 py-2 text-sm font-semibold">Item</th>
                  <th className="px-3 py-2 text-sm font-semibold w-44">
                    Status
                  </th>
                  <th className="px-3 py-2 text-sm font-semibold">Remarks</th>
                  <th className="px-3 py-2 text-sm font-semibold">
                    Suggested Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {document.table_rows.map((row, rowIndex) => (
                  <tr
                    key={`${row.item || "row"}-${rowIndex}`}
                    className="align-top border-t border-slate-200 dark:border-slate-700">
                    <td className="px-3 py-3 font-semibold leading-snug">
                      {formatLabel(row.item || "")}
                    </td>
                    <td className="px-3 py-3 w-44">
                      <StatusBadge>{row.compliance_status}</StatusBadge>
                    </td>
                    <td className="px-3 py-3 leading-relaxed text-slate-700 dark:text-slate-300">
                      {row.remarks_issues}
                    </td>
                    <td className="px-3 py-3 leading-relaxed text-slate-700 dark:text-slate-300">
                      {row.suggested_action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {document.summary_status_report?.suggested_next_steps?.length > 0 && (
          <div className="py-4 border-b border-slate-200 dark:border-slate-700">
            <h5 className="mb-2 font-bold">Suggested Next Steps</h5>
            <ul className="pl-5 space-y-1 leading-relaxed list-disc text-slate-700 dark:text-slate-300">
              {document.summary_status_report.suggested_next_steps.map(
                (step, stepIndex) => (
                  <li key={stepIndex}>{step}</li>
                ),
              )}
            </ul>
          </div>
        )}
      </div>
    ))}

    {data?.summary && (
      <div className="grid gap-3 md:grid-cols-3">
        <div className="p-4 border rounded-lg border-slate-200 dark:border-slate-700 ">
          <p className="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
            Documents
          </p>
          <p className="mt-1 text-lg font-bold leading-snug">
            {data.summary.total_documents ?? 0}
          </p>
        </div>
        <div className="p-4 border rounded-lg border-slate-200 dark:border-slate-700">
          <p className="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
            Top Issue
          </p>
          <p className="mt-1 font-semibold leading-snug">
            {formatLabel(data.summary.top_issues?.[0]?.item || "None")}
          </p>
        </div>
        <div className="p-4 border rounded-lg border-slate-200 dark:border-slate-700">
          <p className="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
            Risk Counts
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(data.summary.risk_counts || {}).map(
              ([risk, count]) => (
                <StatusBadge key={risk}>{`${risk}: ${count}`}</StatusBadge>
              ),
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);

const getDeepResearchPlainText = (deepResearch) => {
  if (!deepResearch) return "";

  if (deepResearch.status === "awaiting_clarification") {
    return deepResearch.clarification_question || "";
  }
  if (deepResearch.status === "failed") {
    return deepResearch.message || "Deep research failed.";
  }
  if (deepResearch.status === "ready_for_research") {
    return "This question hasn't been researched yet.";
  }

  const result = deepResearch.result;
  if (!result) return "";

  const parts = [];
  if (result.summary) parts.push(result.summary);
  (result.sections || []).forEach((section) => {
    if (section.title) parts.push(section.title);
    if (section.content) parts.push(section.content);
    (section.bullets || []).forEach((bullet) => parts.push(`- ${bullet}`));
  });
  if (result.sources?.length) {
    parts.push("Sources:");
    result.sources.forEach((source, index) => {
      parts.push(
        `${index + 1}. ${source.title || source.url || ""}${
          source.url ? ` (${source.url})` : ""
        }`,
      );
    });
  }
  return parts.join("\n\n");
};

const nameActualFileOverOrigin = (text = "", sources = []) => {
  if (!text || !sources?.length) return text;

  return sources.reduce((result, source) => {
    const { title, file, type } = source || {};
    if (!title || !file) return result;

    const origin =
      type === "uploaded_file" ? "uploaded file" : "knowledge base";
    const originLabel = origin.charAt(0).toUpperCase() + origin.slice(1);

    return result
      .replaceAll(`*${title}* (${origin}).`, `*${title}* (${file}).`)
      .replaceAll(`(*${originLabel}: ${title}*)`, `(*${file}: ${title}*)`);
  }, text);
};

// The model now writes its own "Primary Authorities / Sources" section per
// the citation prompt, so the backend's separate "Source documents retrieved"
// block is dropped entirely rather than shown a second time.
const stripSourceDocumentsSection = (text = "") =>
  text.replace(/\n{0,2}###\s*Source documents retrieved[\s\S]*$/, "").trimEnd();

const MarkdownContent = ({ children, className = "" }) => (
  <div className={`markdown-content !p-0 ${className}`}>
    <ReactMarkdown
      components={{
        a: ({ node, ...props }) => (
          <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
      }}
      remarkPlugins={[remarkMath, remarkGfm]}
      rehypePlugins={[rehypeRaw, rehypeKatex]}>
      {children}
    </ReactMarkdown>
  </div>
);

const DeepResearchResult = ({ data, onRetry, retryDisabled }) => {
  if (data.status === "failed" || data.status === "ready_for_research") {
    const isFailed = data.status === "failed";
    return (
      <div
        className={`w-full max-w-4xl p-4 space-y-3 text-sm border rounded-lg ${
          isFailed
            ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/60"
            : "border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/60"
        }`}>
        <div
          className={`flex items-center gap-2 font-semibold ${
            isFailed
              ? "text-red-700 dark:text-red-300"
              : "text-orange-700 dark:text-orange-300"
          }`}>
          <AlertTriangle size={18} />
          <span>
            {isFailed ? "Deep research failed" : "Research hasn't run yet"}
          </span>
        </div>
        <p
          className={
            isFailed
              ? "text-red-700 dark:text-red-200"
              : "text-orange-700 dark:text-orange-200"
          }>
          {isFailed
            ? data.message || "The research provider returned an error."
            : "This question hasn't been researched yet."}
        </p>
        {data.conversation_id && onRetry && (
          <button
            type="button"
            disabled={retryDisabled}
            onClick={() => onRetry(data.conversation_id)}
            className={`flex items-center gap-2 px-3 py-1.5 font-semibold text-white rounded-full disabled:opacity-50 ${
              isFailed
                ? "bg-red-600 hover:bg-red-700"
                : "bg-orange-600 hover:bg-orange-700"
            }`}>
            <RefreshCw size={14} />
            {isFailed ? "Retry research" : "Run research"}
          </button>
        )}
      </div>
    );
  }

  if (data.status === "awaiting_clarification") {
    return (
      <div className="w-full max-w-4xl space-y-2 text-sm">
        <p className="font-semibold text-amber-800 dark:text-amber-200">
          To research this well, I need one more detail:
        </p>
        <MarkdownContent className="text-amber-900 dark:text-amber-100">
          {data.clarification_question}
        </MarkdownContent>
      </div>
    );
  }

  const result = data.result;
  if (!result) return null;

  return (
    <div className="w-full max-w-4xl space-y-4 text-[0.95rem] leading-relaxed">
      {result.summary && (
        <MarkdownContent className="font-medium text-slate-900 dark:text-slate-100">
          {result.summary}
        </MarkdownContent>
      )}

      {result.sections?.map((section, index) => (
        <div
          key={section.title || index}
          className="pt-3 border-t border-slate-200 dark:border-slate-700">
          {section.title && (
            <h4 className="mb-1 font-bold text-slate-900 dark:text-slate-100">
              {section.title}
            </h4>
          )}
          {section.content && (
            <MarkdownContent className="text-slate-700 dark:text-slate-300">
              {section.content}
            </MarkdownContent>
          )}
          {section.bullets?.length > 0 && (
            <ul className="pl-5 mt-2 space-y-1 list-disc text-slate-700 dark:text-slate-300">
              {section.bullets.map((bullet, bulletIndex) => (
                <li key={bulletIndex}>{bullet}</li>
              ))}
            </ul>
          )}
          {section.citations?.length > 0 && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Citations: {section.citations.join(", ")}
            </p>
          )}
        </div>
      ))}

      {result.sources?.length > 0 && (
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <h4 className="mb-2 font-bold text-slate-900 dark:text-slate-100">
            Sources
          </h4>
          <ul className="space-y-1">
            {result.sources.map((source, index) => (
              <li key={index}>
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-green-700 underline dark:text-green-400">
                    {source.title || source.url}
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <span>{source.title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MessageList = React.memo(
  ({
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    messagesEndRef,
    hasError,
    setHasError,
    handleRegenerate,
    stopStream,
    didSearch,
    setDidSearch,
    linkSearch,
    setLinkSearch,
    data,
    setData,
    messageId,
    setMessageId,
    threadId,
    handleSendMessage,
    setRunId,
    eventSourceRef,
    setWait,
    eventError,
    botRole,
    wait,
    isRecording,
    setIsRecording,
    token,
    onRetryDeepResearch,
    deepResearchLoading,
  }) => {
    const [previewImages, setPreviewImages] = useState([]);
    const [previewIndex, setPreviewIndex] = useState();
    const [copyIndex, setCopyIndex] = useState(null);
    const [brokenImageUrls, setBrokenImageUrls] = useState({});
    const [download, setDownload] = useState(false);
    const [downloadingIndex, setDownloadingIndex] = useState(null);
    const [downloadingModal, setDownloadingModal] = useState(null);
    const [deepResearchDownloadingId, setDeepResearchDownloadingId] =
      useState(null);

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return "";
      return new Date(timestamp).toLocaleString();
    };

    const getPdfFilename = (headers, fallback) => {
      const disposition = headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      return match ? match[1] : fallback;
    };

    const triggerPdfDownload = (blob, headers, fallbackFilename) => {
      const pdfUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getPdfFilename(headers, fallbackFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
    };

    const getPdfErrorMessage = async (err) => {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          return JSON.parse(text)?.detail || text;
        } catch {
          return err.message;
        }
      }
      return err.message;
    };

    const handleGeneratePDF = async () => {
      try {
        setDownload(true);
        const response = await api.get(
          `/v2/responses-chat/conversation/${threadId}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        triggerPdfDownload(
          response.data,
          response.headers,
          `thread_${threadId}.pdf`,
        );
      } catch (err) {
        console.error("Error generating PDF:", await getPdfErrorMessage(err));
      } finally {
        setDownload(false);
        setDownloadingModal(null);
      }
    };

    const handleGenerateMessagePDF = async (index, message_id) => {
      try {
        setDownloadingIndex(index);
        const response = await api.get(
          `/v2/responses-chat/conversation/${threadId}/message/${message_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        triggerPdfDownload(
          response.data,
          response.headers,
          `thread_${threadId}_message_${message_id}.pdf`,
        );
      } catch (err) {
        console.error(
          "Error generating message PDF:",
          await getPdfErrorMessage(err),
        );
      } finally {
        setDownloadingIndex(null);
        setDownloadingModal(null);
      }
    };

    const handleDownloadDeepResearchPdf = async (conversationId) => {
      try {
        setDeepResearchDownloadingId(conversationId);
        const response = await api.get(
          `/v3/deep-research/${conversationId}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        triggerPdfDownload(
          response.data,
          response.headers,
          `research-${conversationId}.pdf`,
        );
      } catch (err) {
        console.error(
          "Error generating deep research PDF:",
          await getPdfErrorMessage(err),
        );
      } finally {
        setDeepResearchDownloadingId(null);
      }
    };

    const pretty = (s) =>
      String(s || "")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

    return (
      <>
        {messages.map((message, index) => {
          const structuredMessage =
            message.role === "assistant"
              ? parseStructuredMessage(message.text_value)
              : null;
          const imageAttachments = [
            ...(Array.isArray(message.image_file_id)
              ? message.image_file_id.map((fileId, idx) => ({
                  name: `uploaded-img-${idx}`,
                  url: `https://${baseURL}/chat/openai-image/${fileId}`,
                  file_id: fileId,
                }))
              : []),
            ...(Array.isArray(message.image) ? message.image : []),
          ].filter((img) => img?.url);
          const messageFiles = Array.isArray(message.file)
            ? message.file
            : Array.isArray(message.filenames) &&
                message.filenames !== "No File"
              ? message.filenames
              : [];

          return (
            <div className="w-full max-w-5xl mt-4 place-self-center">
              <div className="z-50 mr-7">
                {imageAttachments.length > 0 && (
                  <div className="flex flex-wrap items-start justify-end gap-2 mb-1">
                    {imageAttachments.map((img, idx) => {
                      const imageUrl = img.url;
                      const imageName = getAttachmentName(img);

                      if (brokenImageUrls[imageUrl]) {
                        return (
                          <FileAttachmentCard
                            key={`${imageUrl}-${idx}`}
                            file={img}
                          />
                        );
                      }

                      return (
                        <div
                          key={`${imageUrl}-${idx}`}
                          onClick={() => {
                            const urls = imageAttachments.map(
                              (image) => image.url,
                            );
                            setPreviewImages(urls);
                            setPreviewIndex(idx);
                          }}
                          className="overflow-hidden border-none rounded cursor-pointer max-w-64 max-h-64">
                          <img
                            src={imageUrl}
                            alt={imageName}
                            onError={() =>
                              setBrokenImageUrls((current) => ({
                                ...current,
                                [imageUrl]: true,
                              }))
                            }
                            className="object-contain h-fit w-fit"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                {messageFiles.map((file, idx) => (
                  <FileAttachmentCard
                    key={`${getAttachmentName(file)}-${idx}`}
                    file={file}
                  />
                ))}
                {previewImages !== null && previewImages.length > 0 && (
                  <>
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-25"
                      onClick={() => {
                        setPreviewImages([]);
                        setPreviewIndex(null);
                      }}>
                      <div
                        className="absolute top-0 right-0 p-1 mt-8 mr-8 bg-red-600 rounded-full cursor-pointer hover:bg-red-400"
                        onClick={() => {
                          setPreviewImages([]);
                          setPreviewIndex(null);
                        }}>
                        <X
                          className="text-black size-5"
                          strokeWidth={3}
                        />
                      </div>
                      {/* Previous button */}
                      {previewIndex > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewIndex((prev) =>
                              prev !== null ? prev - 1 : null,
                            );
                          }}
                          className="px-4 py-2 mr-4 text-3xl text-white rounded left-4 hover:bg-black/70">
                          ‹
                        </button>
                      )}

                      <img
                        src={previewImages[previewIndex]}
                        alt="Preview"
                        className="max-w-[90%] max-h-[90%]"
                        onClick={(e) => e.stopPropagation()}
                      />

                      {/* Next button */}
                      {previewIndex < previewImages.length - 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewIndex((prev) =>
                              prev !== null ? prev + 1 : null,
                            );
                          }}
                          className="px-4 py-2 ml-4 text-3xl text-white rounded right-4 hover:bg-black/70">
                          ›
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
              <>
                <div
                  key={index}
                  className={`p-1 pt-0 gap-x-2.5 rounded flex ${
                    message.sender === "assistant" ||
                    message.role === "assistant"
                      ? "justify-start"
                      : "justify-end"
                  } w-full`}>
                  {(message.sender === "assistant" ||
                    message.role === "assistant") && (
                    <div className="flex flex-col items-center mt-5">
                      <img
                        src={mainLogo}
                        className="w-6 h-6 max-w-sm mr-1 "
                        alt="Logo"
                      />
                    </div>
                  )}
                  <div
                    className={`sm:max-w-[100%] rounded-xl rounded-tr-sm ${
                      message.sender === "assistant" ||
                      message.role === "assistant"
                        ? "bg-slate-50 dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                        : "bg-green-300 dark:bg-green-800 dark:text-gray-200"
                    }`}
                    style={{
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}>
                    {message.deepResearch?.status === "loading" ? (
                      <LoadingText
                        text="Researching your question — this can take a few minutes..."
                        isLoading={true}
                      />
                    ) : message.role === "assistant" &&
                      index === messages.length - 1 &&
                      isLoading ? (
                      <LoadingText
                        text={
                          message.dr_step && message.dr_status
                            ? `${pretty(message.dr_step)} • ${pretty(
                                message.dr_status,
                              )}`
                            : message.isAnalyzing
                              ? "Analyzing message..."
                              : message.isProcessing
                                ? "Processing..."
                                : "Please wait..."
                        }
                        isLoading={isLoading}
                      />
                    ) : (
                      <div
                        className={`markdown-content ${
                          message.role === "user" ? "markdown-user" : ""
                        } ${
                          message.isStreamError
                            ? "font-semibold text-red-600 dark:text-red-400"
                            : ""
                        }`}>
                        {eventError && (
                          <EventError
                            index={index}
                            message={message}
                            messages={messages}
                          />
                        )}
                        {message.deepResearch &&
                        message.deepResearch.status !== "loading" ? (
                          <DeepResearchResult
                            data={message.deepResearch}
                            onRetry={onRetryDeepResearch}
                            retryDisabled={deepResearchLoading}
                          />
                        ) : structuredMessage?.type === "error" ? (
                          <FriendlyErrorResponse
                            error={structuredMessage.data}
                          />
                        ) : structuredMessage?.type === "invoice" ? (
                          <StructuredInvoiceResponse
                            data={structuredMessage.data}
                          />
                        ) : (
                          <>
                            <ReactMarkdown
                              components={{
                                a: ({ node, ...props }) => (
                                  <a
                                    {...props}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  />
                                ),
                              }}
                              className={`${
                                message.role === "user" ? "pb-0" : ""
                              }`}
                              remarkPlugins={[remarkMath, remarkGfm]}
                              rehypePlugins={[rehypeRaw, rehypeKatex]}>
                              {message.role === "assistant"
                                ? nameActualFileOverOrigin(
                                    stripSourceDocumentsSection(
                                      message.text_value,
                                    ),
                                    message.sources,
                                  )
                                : message.text_value}
                            </ReactMarkdown>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {(message.sender === "user" || message.role === "user") && (
                    <FontAwesomeIcon
                      icon={faUser}
                      className="mt-3 text-green-500 dark:text-green-600"
                    />
                  )}
                </div>
                <div
                  className={`${message.role === "assistant" && "-mt-2 ml-2"}`}>
                  <div
                    className={`text-xs ${
                      message.role === "assistant"
                        ? "text-left ml-12 text-gray-600 flex items-center gap-2 dark:text-gray-400"
                        : "text-right mr-7 mt-1 text-gray-600 flex justify-end items-center gap-2 dark:text-gray-300"
                    }`}>
                    {formatTimestamp(message.timestamp)}
                    {message.timestamp && (
                      <>
                        <button
                          onClick={async () => {
                            let htmlContent;
                            let plainText;

                            if (message.deepResearch) {
                              plainText = getDeepResearchPlainText(
                                message.deepResearch,
                              );
                              htmlContent = plainText
                                .split("\n")
                                .map((line) => `<p>${line}</p>`)
                                .join("");
                            } else {
                              const markdownContainer =
                                document.createElement("div");
                              markdownContainer.innerHTML =
                                document.querySelectorAll(".markdown-content")[
                                  index
                                ]?.innerHTML;

                              htmlContent = markdownContainer.innerHTML.trim();
                              plainText =
                                nameActualFileOverOrigin(
                                  stripSourceDocumentsSection(
                                    message.text_value,
                                  ),
                                  message.sources,
                                )
                                  .replace(/^File\/s:.*$/gim, "")
                                  .replace(/【\d+:\d+†.*?】/g, "")
                                  .replace(/(\r?\n){2,}/g, "\n\n");
                            }

                            try {
                              const clipboardItems = [
                                new ClipboardItem({
                                  "text/html": new Blob([htmlContent], {
                                    type: "text/html",
                                  }),
                                  "text/plain": new Blob([plainText], {
                                    type: "text/plain",
                                  }),
                                }),
                              ];
                              await navigator.clipboard.write(clipboardItems);
                            } catch (err) {
                              console.error("Failed to copy:", err);
                              await navigator.clipboard.writeText(plainText);
                            }
                            setCopyIndex(index);
                            setTimeout(() => setCopyIndex(null), 3000);
                          }}
                          className="p-1 text-gray-700 rounded dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-slate-700"
                          title="Copy response">
                          {copyIndex === index ? (
                            <Check
                              size={16}
                              strokeWidth={2.5}
                            />
                          ) : (
                            <Copy
                              size={16}
                              strokeWidth={2.5}
                            />
                          )}
                        </button>
                        {message.deepResearch
                          ? message.deepResearch.status === "completed" &&
                            message.deepResearch.conversation_id && (
                              <button
                                className={`flex items-center gap-1 p-1 text-gray-700 rounded dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-slate-700 ${
                                  deepResearchDownloadingId ===
                                    message.deepResearch.conversation_id &&
                                  "pointer-events-none"
                                }`}
                                title="Download research report as PDF"
                                onClick={() =>
                                  handleDownloadDeepResearchPdf(
                                    message.deepResearch.conversation_id,
                                  )
                                }>
                                {deepResearchDownloadingId ===
                                message.deepResearch.conversation_id ? (
                                  <div className="p-1.5 border-2 border-t-0 border-gray-700 rounded-full animate-spin" />
                                ) : (
                                  <Download
                                    size={16}
                                    strokeWidth={2.5}
                                  />
                                )}
                              </button>
                            )
                          : message.role === "assistant" && (
                              <button
                                className={`flex relative gap-1 p-1 text-gray-700 rounded dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-slate-700 ${
                                  downloadingIndex === index &&
                                  "pointer-events-none"
                                }`}
                                onClick={() => {
                                  if (downloadingModal === index) {
                                    setDownloadingModal(null);
                                  } else {
                                    setDownloadingModal(index);
                                  }
                                }}>
                                {download || downloadingIndex === index ? (
                                  <div className="p-1.5 border-2 border-t-0 border-gray-700 rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <Download
                                      size={16}
                                      strokewidth={2.5}
                                    />
                                    <ChevronDown
                                      size={16}
                                      strokeWidth={2.5}
                                    />
                                  </>
                                )}
                                {downloadingModal === index && (
                                  <div className="absolute whitespace-nowrap p-1 text-left  flex flex-col -translate-x-1/2 translate-y-14 rounded-md left-1/2 bottom-0 bg-slate-200 dark:bg-slate-600 space-y-0.5">
                                    <button
                                      className={`py-1 px-2 text-left w-full text-gray-800 rounded dark:text-gray-300 hover:bg-slate-400 dark:hover:bg-slate-700 ${
                                        downloadingIndex === index
                                          ? "pointer-events-none"
                                          : ""
                                      }`}
                                      title="Download this message as PDF"
                                      onClick={() =>
                                        handleGenerateMessagePDF(
                                          index,
                                          message.id,
                                        )
                                      }>
                                      <span className="whitespace-nowrap">
                                        Download this message as PDF
                                      </span>
                                    </button>
                                    <button
                                      className={`py-1 px-2 text-gray-800 rounded dark:text-gray-300 hover:bg-slate-400 dark:hover:bg-slate-700 ${
                                        download && "pointer-events-none"
                                      }`}
                                      title="Download chat history"
                                      onClick={() => {
                                        handleGeneratePDF();
                                      }}>
                                      <span>
                                        Download entire chat thread as PDF
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </button>
                            )}
                      </>
                    )}
                  </div>
                  <div className="ml-12">
                    {message?.role === "assistant" && !eventError && (
                      <Feedback threadId={threadId} />
                    )}
                  </div>
                </div>
              </>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </>
    );
  },
);

export default MessageList;
